import { markRaw } from 'vue'
import RecordRTC from 'recordrtc'

import MultipartVideoUploader from '../../common/multipart-video-uploader'
import { recordContentType, recordMimeType } from './recorder-type'

export default {
  props: {
    uploadWhileRecording: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'upload-progress',
    'upload-completed'
  ],
  data () {
    return {
      filename: null,
      recorder: null,
      uploader: null,

      recorderState: null,

      pendingBlob: null,

      progress: 3,

      isSave: false,
      isCompleting: false,

      recordFileType: 'webm'
    }
  },
  computed: {
    isStopped () {
      return this.recorderState === 'stopped'
    },
    recordContentType () {
      return recordContentType(this.recordFileType)
    },
    recordMimeType () {
      return recordMimeType(this.recordFileType)
    }
  },
  methods: {
    async initUploader () {
      this.uploader = markRaw(new MultipartVideoUploader(this.filename, {
        disk: Spark.getDisk(),
        contentType: this.recordContentType
      }))

      this.uploader.addEventListener('progress', e => {
        this.progress = e.detail.progress

        if (this.isStopped) {
          this.$emit('upload-progress', this.progress)
        }
      })
    },

    mergePendingBlob (blob) {
      if (!this.pendingBlob) {
        this.pendingBlob = blob
        return
      }

      this.pendingBlob = new Blob(
        [this.pendingBlob, blob],
        { type: this.recordContentType }
      )
    },

    async stopUploadBlob () {
      if (this.isCompleting) {
        return
      }

      this.isCompleting = true

      if (this.isSave) {
        this.$emit('upload-progress', this.progress)

        // R2 requires last part must be smaller than the chunk size
        while (this.pendingBlob.size > MultipartVideoUploader.DEFAULT_FILE_CHUNK_SIZE) {
          const uploadingPart = this.pendingBlob.slice(0, MultipartVideoUploader.DEFAULT_FILE_CHUNK_SIZE)
          this.pendingBlob = this.pendingBlob.slice(MultipartVideoUploader.DEFAULT_FILE_CHUNK_SIZE)
          await this.uploader.uploadPart(uploadingPart)
        }

        await this.uploader.uploadPart(this.pendingBlob)
        await this.uploader.complete(true)

        const videoPath = this.uploader.key
        const previewValue = await this.uploader.uploadVideoPreview()
        this.$emit('upload-completed', videoPath, previewValue)
      } else {
        this.uploader.abort()
      }

      this.resetUploader()
    },

    handleDataAvailable (blob) {
      if (blob.size <= 0) {
        window.SwalTimer.fire({
          icon: 'error',
          title: 'The recording data size is 0.'
        })

        this.stopRecorder()
        return
      }

      this.mergePendingBlob(blob)

      if (this.isStopped) {
        this.stopUploadBlob()
        return
      }

      if (this.pendingBlob.size >= MultipartVideoUploader.DEFAULT_FILE_CHUNK_SIZE) {
        // R2 requires each part to be the same size (except for the last part)
        const isEquals = this.pendingBlob.size === MultipartVideoUploader.DEFAULT_FILE_CHUNK_SIZE
        const uploadingPart = this.pendingBlob.slice(0, MultipartVideoUploader.DEFAULT_FILE_CHUNK_SIZE)
        this.pendingBlob = isEquals ? null : this.pendingBlob.slice(MultipartVideoUploader.DEFAULT_FILE_CHUNK_SIZE)
        this.uploader.uploadPart(uploadingPart)
      }
    },

    handleStateChanged (state) {
      this.recorderState = state
    },

    initFilename () {
      this.filename = this.generateFileName(this.recordFileType)
    },

    initRecorder (stream) {
      const options = {
        type: 'video',
        disableLogs: false,
        ignoreMutedMedia: false,
        mimeType: this.recordMimeType,
        timeSlice: 5000
      }

      this.pendingBlob = null

      if (this.uploadWhileRecording) {
        this.initUploader()
        options.ondataavailable = this.handleDataAvailable.bind(this)
      }

      this.recorder = markRaw(RecordRTC(stream, options))
      this.recorder.stream = stream
      this.recorder.onStateChanged = this.handleStateChanged.bind(this)
    },

    startRecorder (stream, fileType = 'webm') {
      this.initFilename()

      this.recordFileType = fileType
      this.initRecorder(stream)

      this.recorder.startRecording()
    },

    /**
     * @param {boolean} isSave
     * @returns {Promise<File>}
     */
    stopRecorder (isSave = false) {
      this.isSave = isSave

      return new Promise(resolve => {
        this.recorder.stopRecording(() => {
          const file = new File([
            this.recorder.getBlob()
          ], this.filename, {
            type: this.recordContentType
          })

          resolve(file)
        })
      })
    },

    resetUploader () {
      this.filename = null
      this.pendingBlob = null
      this.progress = 3
      this.isSave = false
      this.isCompleting = false
      this.recorderState = null
    },

    generateFileName (fileExtension) {
      const d = new Date()
      const year = d.getUTCFullYear()
      const month = d.getUTCMonth()
      const date = d.getUTCDate()
      const randomStr = (Math.random() * new Date().getTime()).toString(36).replace(/\./g, '')
      return 'Dubb-' + year + month + date + '-' + randomStr + '.' + fileExtension
    }
  }
}
